import i18n from "i18next";

export const LANGUAGE_STORAGE_KEY = "i18nextLng";
export const LANGUAGE_OVERRIDE_SESSION_KEY = "languagePreferenceChanged";

export const SUPPORTED_LANGUAGES = ["en", "ar", "ckb"] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

const RTL_LANGUAGES = new Set<SupportedLanguage>(["ar", "ckb"]);

export const normalizeLanguage = (
  lang?: string | null,
): SupportedLanguage => {
  const normalized = lang?.trim().toLowerCase();
  if (normalized?.startsWith("ar")) return "ar";
  if (normalized?.startsWith("ckb")) return "ckb";
  return "en";
};

export const isRtlLanguage = (lang?: string | null) =>
  RTL_LANGUAGES.has(normalizeLanguage(lang));

export const getIntlLocale = (lang?: string | null) => {
  const normalizedLanguage = normalizeLanguage(lang);
  if (normalizedLanguage === "ar") return "ar-SA";
  if (normalizedLanguage === "ckb") return "ckb-IQ";
  return "en-US";
};

export const normalizeOptionalLanguage = (lang?: string | null) => {
  return lang?.trim() ? normalizeLanguage(lang) : null;
};

export const getPersistedLanguage = () => {
  const candidates = [
    typeof window !== "undefined"
      ? window.localStorage.getItem(LANGUAGE_STORAGE_KEY)
      : null,
    typeof document !== "undefined" ? document.documentElement.lang : null,
    i18n.resolvedLanguage,
    i18n.language,
    typeof navigator !== "undefined" ? navigator.language : null,
  ];

  const language = candidates.find((candidate) => candidate?.trim());
  return normalizeLanguage(language);
};

export const applyLanguageToDocument = (lang: string) => {
  const normalizedLanguage = normalizeLanguage(lang);

  if (typeof document !== "undefined") {
    document.documentElement.dir = isRtlLanguage(normalizedLanguage)
      ? "rtl"
      : "ltr";
    document.documentElement.lang = normalizedLanguage;
  }

  return normalizedLanguage;
};

export const changeLanguage = async (
  lang: string,
  { markAsUserChange = true }: { markAsUserChange?: boolean } = {},
) => {
  const normalizedLanguage = normalizeLanguage(lang);

  if (typeof window !== "undefined") {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, normalizedLanguage);
    if (markAsUserChange) {
      sessionStorage.setItem(LANGUAGE_OVERRIDE_SESSION_KEY, "true");
    }
  }

  applyLanguageToDocument(normalizedLanguage);

  await i18n.changeLanguage(normalizedLanguage);

  return normalizedLanguage;
};
